import type { UUID } from '@aline/common';
import type { ApolloCache, Reference } from '@apollo/client';
import { Apollo } from 'src/gql/generated';

const PERMISSION_TYPENAME = 'WorkflowPermission';

// A type alias, not an interface, so it satisfies Apollo's indexed `StoreObject`.
type WorkflowActionCacheKey = {
  __typename: 'Task' | 'WorkflowRecipient';
  id: UUID;
};

/**
 * Drops deleted signers and tasks from the cache. Apollo filters a dangling reference out of
 * every list that held it, so a collaborator's actions, a step's actions, a workflow's unused
 * actions and its task lists all update without naming each one.
 */
export function removeWorkflowActionsFromCache(
  cache: ApolloCache,
  actions: readonly WorkflowActionCacheKey[]
): void {
  for (const action of actions) {
    const id = cache.identify(action);
    if (id) cache.evict({ id });
  }
}

/** The collaborator a cached signer belongs to. Read it before a reassign writes the new one. */
export function readRecipientPermissionId(cache: ApolloCache, recipientId: UUID): UUID | null {
  const recipient = cache.readFragment<Apollo.WorkflowRecipientPermissionIdFragment>({
    fragment: Apollo.WorkflowRecipientPermissionIdFragmentDoc,
    id: cache.identify({ __typename: 'WorkflowRecipient', id: recipientId }),
  });
  return recipient?.permission.id ?? null;
}

/**
 * Moves a reassigned signer or task from one collaborator's actions to another's. A collaborator
 * whose actions were never loaded is left alone.
 */
export function moveWorkflowActionInCache(
  cache: ApolloCache,
  options: { action: WorkflowActionCacheKey; fromPermissionId: UUID; toPermissionId: UUID }
): void {
  const { action, fromPermissionId, toPermissionId } = options;
  const actionCacheId = cache.identify(action);
  if (fromPermissionId === toPermissionId || !actionCacheId) return;

  cache.modify({
    fields: {
      actions: (refs: readonly Reference[]) => refs.filter(ref => ref.__ref !== actionCacheId),
    },
    id: cache.identify({ __typename: PERMISSION_TYPENAME, id: fromPermissionId }),
  });
  cache.modify({
    fields: {
      actions: (refs: readonly Reference[], { toReference }) => {
        const actionRef = toReference(action);
        if (!actionRef || refs.some(ref => ref.__ref === actionCacheId)) return refs;
        return [...refs, actionRef];
      },
    },
    id: cache.identify({ __typename: PERMISSION_TYPENAME, id: toPermissionId }),
  });
}
